import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input
} from '@angular/core';
import numeral from 'numeral';
import { Metric } from '../key-metrics.component';
import { UiQuery } from '../../../state/ui/ui.query';

@Component({
    selector: 'app-metric',
    template: `
        <dt [attr.data-cy]="'metric-' + metric.label">{{ metric.label }}</dt>
        <dd
            [attr.data-cy]="'metric-value-' + metric.label"
            *ngIf="metric.format"
            [style]="value"
            [class]="'metric-' + metric.type"
            [ngClass]="{
                metric: !isCreditsKeyMetrics,
                creditsMetric: isCreditsKeyMetrics && !isPrintMode,
                creditsMetricPrintMode: isCreditsKeyMetrics && isPrintMode
            }"
            [class.metric-animated]="metric.format && cssPropertySupport"
            [title]="metric.value"
        ></dd>
        <dd
            *ngIf="!metric.format"
            [ngClass]="{
                metric: !isCreditsKeyMetrics,
                creditsMetric: isCreditsKeyMetrics && !isPrintMode,
                creditsMetricPrintMode: isCreditsKeyMetrics && isPrintMode
            }"
        >
            <div
                *ngIf="
                    isCreditsKeyMetrics && metric.value && metric.value.includes('<br/>');
                    else default
                "
            >
                <div [innerHTML]="metric.value"></div>
            </div>

            <ng-template #default>
                {{ metric.value }}
            </ng-template>
        </dd>
    `,
    styleUrls: ['./metric.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MetricComponent {
    get value(): MetricValue {
        return this._value;
    }

    set value(value: MetricValue) {
        this._value = value;
        this._cdr.detectChanges();
    }
    get metric() {
        return this._metric;
    }

    @Input() isCreditsKeyMetrics: boolean = false;
    @Input() isPrintMode: boolean = false;

    @Input() set metric(metric) {
        this._metric = metric;
        setTimeout(
            () =>
                (this.value = this.displayValue(
                    metric.value,
                    metric.format,
                    metric.prefix,
                    metric.suffix
                )),
            0
        );
    }

    private _metric: Metric;
    public cssPropertySupport = this.uiQuery.getValue().cssPropertySupport;

    constructor(private _cdr: ChangeDetectorRef, private uiQuery: UiQuery) {}

    displayValue(value, format, prefix, suffix) {
        const prettyValue = numeral(value).format(format);
        const {
            groups: { sign, integer, fraction, abbreviation }
        } = prettyValue.match(
            /(?<sign>-)?(?<integer>\d*).(?<fraction>\d*)(?<abbreviation>.*)/
        );
        const fractionInteger = +fraction.toString().replace(/\b[0.]*/, '');
        const leadingZeros = prettyValue?.startsWith('-')
            ? ''
            : Number.isInteger(fraction)
            ? 0
            : (fraction + '').length - (fractionInteger + '').length;
        return {
            '--integer': +integer,
            '--fraction': +fraction,
            '--leading-zeros': leadingZeros,
            '--sign': "'" + (sign || '') + "'",
            '--prefix': "'" + (prefix || '') + "'",
            '--suffix': "'" + (abbreviation || suffix || '') + "'",
            '--fallback': "'" + prettyValue + "'"
        };
    }

    private _value: MetricValue = {
        '--integer': 0,
        '--fraction': 0,
        '--leading-zeros': 0,
        '--suffix': '',
        '--prefix': '',
        '--sign': ''
    };
}

interface MetricValue {
    '--fraction': number;
    '--leading-zeros': number | string;
    '--integer': number;
    '--suffix': string;
    '--prefix': string;
    '--sign': string;
}
